import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminAction } from '../components/PermissionGate';
import { useAuth } from '../hooks/useAuth';
import {
  cancelQaRun,
  createQaRun,
  fetchQaOptions,
  fetchQaRunLive,
  fetchQaRuns,
  fetchQaTests,
} from './api';
import {
  backendVersionLabel,
  formatCount,
  formatDuration,
  formatSeconds,
  formatTimestamp,
  runStatusClasses,
} from './format';
import type { QaLiveResponse, QaOptions, QaRun, QaTestDefinition } from './types';
import { isQaTerminal, QA_APPS } from './types';

const LIVE_POLL_MS = 3000;

/**
 * Run test: launch a saved definition and watch it.
 *
 * Progress comes from a single `/live` request per poll — the server fans out to
 * every loaded application — so adding applications to a test does not multiply
 * the browser's request rate.
 */
export function RunTestPage() {
  const { canDestroy } = useAuth();
  const [params, setParams] = useSearchParams();
  const runIdParam = params.get('run');
  const testParam = params.get('test');

  const [options, setOptions] = useState<QaOptions | null>(null);
  const [definitions, setDefinitions] = useState<QaTestDefinition[]>([]);
  const [selectedId, setSelectedId] = useState(testParam || '');
  const [activeRunId, setActiveRunId] = useState<string | null>(runIdParam);
  const [live, setLive] = useState<QaLiveResponse | null>(null);
  const [recent, setRecent] = useState<QaRun[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Kept apart from `error`: a refused start attaches the monitor to the run that
  // blocked it, and that poll succeeding would otherwise clear the explanation of
  // why the start was refused in the first place.
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [opts, tests, runs] = await Promise.all([
          fetchQaOptions(),
          fetchQaTests(),
          fetchQaRuns(20),
        ]);
        if (cancelled) return;
        setOptions(opts);
        setDefinitions(tests);
        setRecent(runs);
        setSelectedId((current) => current || tests[0]?.id || '');
        // Landing here without a run id: attach to whatever is still going, so a
        // reload or a hand-off between people does not lose the monitor.
        if (!runIdParam) {
          const inFlight = runs.find((run) => !isQaTerminal(run.status));
          if (inFlight) setActiveRunId(inFlight.id);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally mount-only: `runIdParam` is seeded into state above and
    // re-reading it here would restart the initial load on every URL change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live polling. Self-rescheduling rather than an interval so a slow fan-out
  // read cannot stack up requests, and so reaching a terminal status simply
  // stops the loop.
  useEffect(() => {
    if (!activeRunId) return;
    let cancelled = false;
    let timer = 0;

    const tick = async () => {
      let done = false;
      try {
        const snapshot = await fetchQaRunLive(activeRunId);
        if (cancelled) return;
        setLive(snapshot);
        setError(null);
        done = isQaTerminal(snapshot.run.status);
        if (done) {
          // One last list read so the recent-runs table shows the final status.
          const runs = await fetchQaRuns(20);
          if (!cancelled) setRecent(runs);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
      if (!cancelled && !done) timer = window.setTimeout(tick, LIVE_POLL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeRunId]);

  const selected = useMemo(
    () => definitions.find((d) => d.id === selectedId) || null,
    [definitions, selectedId],
  );

  // Only one run may be in flight at a time, and it is not necessarily the one
  // being monitored — another operator may have started it, or this page may be
  // showing an older run pulled up from the history.
  const activeRun = useMemo(() => recent.find((item) => !isQaTerminal(item.status)) || null, [
    recent,
  ]);

  const selectedSummary = useMemo(() => {
    if (!selected) return null;
    const apps = QA_APPS.filter((app) => selected.per_app_config?.[app]?.enabled);
    return {
      apps,
      // A scan application's load is its CSV; only gen-api, which has no CSV, is
      // told how many calls to make.
      units: apps.reduce(
        (sum, app) =>
          sum +
          (app === 'genapi'
            ? selected.per_app_config?.genapi?.count || 0
            : app === 'served_models'
              ? (selected.per_app_config?.served_models?.workloads || []).reduce(
                  (total, workload) => total + workload.count,
                  0,
                )
            : selected.scan_ids_by_app?.[app]?.length || 0),
        0,
      ),
      realSpend: apps.filter(
        (app) => app !== 'served_models' && !selected.per_app_config?.[app]?.dummy,
      ),
    };
  }, [selected]);

  const monitor = useCallback(
    (runId: string) => {
      setLive(null);
      setActiveRunId(runId);
      setParams({ run: runId }, { replace: true });
    },
    [setParams],
  );

  const start = useCallback(async () => {
    if (!selectedId) return;
    setStarting(true);
    setStartError(null);
    setWarnings([]);
    try {
      const created = await createQaRun(selectedId);
      setWarnings(created.warnings);
      monitor(created.id);
      setRecent(await fetchQaRuns(20));
    } catch (e) {
      setStartError(e instanceof Error ? e.message : String(e));
      // A refusal is almost always "something else is already running", and this
      // page's idea of what that is may be stale. Re-read and attach to it so the
      // operator can see what they are waiting for.
      const runs: QaRun[] | null = await fetchQaRuns(20).catch(() => null);
      if (runs) {
        setRecent(runs);
        const inFlight = runs.find((item) => !isQaTerminal(item.status));
        if (inFlight) monitor(inFlight.id);
      }
    } finally {
      setStarting(false);
    }
  }, [monitor, selectedId]);

  const cancel = useCallback(async () => {
    if (!activeRunId) return;
    setCancelling(true);
    try {
      await cancelQaRun(activeRunId);
      setLive(await fetchQaRunLive(activeRunId));
      setRecent(await fetchQaRuns(20));
    } catch (e) {
      setStartError(e instanceof Error ? e.message : String(e));
    } finally {
      setCancelling(false);
    }
  }, [activeRunId]);

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  }

  const run = live?.run;
  const running = run ? !isQaTerminal(run.status) : false;
  const busy = running || activeRun !== null;
  const labelFor = (key: string) =>
    options?.applications.find((a) => a.key === key)?.label || key;

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Run test</h1>
        <p className="text-sm text-slate-500 mt-1">
          Pick a saved test and watch each application's queue drain. Progress refreshes every{' '}
          {LIVE_POLL_MS / 1000} seconds.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
        {definitions.length === 0 ? (
          <p className="text-sm text-slate-500">
            No saved tests yet.{' '}
            <Link to="/qa/bank" className="text-indigo-600 font-medium hover:text-indigo-700">
              Create one in the tests bank
            </Link>
            .
          </p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <label className="block flex-1">
                <span className="text-xs font-medium text-slate-600">Test</span>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
                >
                  {definitions.map((definition) => (
                    <option key={definition.id} value={definition.id}>
                      {definition.name} (v{definition.version})
                    </option>
                  ))}
                </select>
              </label>
              <AdminAction>
                <button
                  onClick={start}
                  disabled={starting || busy || !selectedId}
                  className="px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {starting ? 'Starting…' : busy ? 'A test is running' : 'Start test'}
                </button>
              </AdminAction>
            </div>

            {selectedSummary && (
              <p className="mt-3 text-sm text-slate-500">
                {selectedSummary.apps.length === 0
                  ? 'This test has no applications enabled, so there is nothing to run.'
                  : `Loads ${selectedSummary.apps
                      .map(labelFor)
                      .join(', ')} with ${selectedSummary.units.toLocaleString()} units in total.`}
              </p>
            )}
            {busy && (
              <p className="mt-3 text-xs text-slate-500">
                Only one test runs at a time, so that a test measures its own load and nothing
                else.{' '}
                {activeRun && activeRun.id !== activeRunId ? (
                  <>
                    &quot;{activeRun.definition_name}&quot; run #{activeRun.run_number} is going
                    now —{' '}
                    <button
                      onClick={() => monitor(activeRun.id)}
                      className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      monitor it
                    </button>
                    .
                  </>
                ) : (
                  'The next one can start when this one finishes, or as soon as it is cancelled.'
                )}
              </p>
            )}
            {!canDestroy && (
              <p className="mt-3 text-xs text-slate-500">
                Starting a test needs an administrator account. Runs in progress are still visible
                here.
              </p>
            )}
            {selectedSummary && selectedSummary.realSpend.length > 0 && (
              <div className="mt-3 rounded-xl border border-warning-200 bg-warning-50 text-warning-700 px-3 py-2 text-xs">
                Dummy mode is off for {selectedSummary.realSpend.map(labelFor).join(', ')}. This run
                will spend real money.
              </div>
            )}
          </>
        )}

        {warnings.map((warning) => (
          <div
            key={warning}
            className="mt-3 rounded-xl border border-warning-200 bg-warning-50 text-warning-700 px-3 py-2 text-xs"
          >
            {warning}
          </div>
        ))}
        {[startError, error].filter(Boolean).map((message) => (
          <div
            key={message}
            className="mt-3 rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm"
          >
            {message}
          </div>
        ))}
      </div>

      {run && live && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                {run.definition_name}
                <span className="ml-2 text-xs font-normal text-slate-400">
                  v{run.definition_version} · run #{run.run_number}
                </span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Started {formatTimestamp(run.started_at || run.created_at)} · backend{' '}
                {backendVersionLabel(run.backend_versions)} · running for{' '}
                {formatDuration(run.started_at, run.finished_at)}
                {run.last_heartbeat_at && running
                  ? ` · last heartbeat ${formatTimestamp(run.last_heartbeat_at)}`
                  : ''}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${runStatusClasses(run.status)}`}
              >
                {run.status}
              </span>
              {running && (
                <AdminAction>
                  <button
                    onClick={cancel}
                    disabled={cancelling}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs hover:bg-slate-50 disabled:opacity-50 transition-colors"
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel'}
                  </button>
                </AdminAction>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-6 divide-x divide-slate-100 border-b border-slate-100">
            {(
              [
                ['Planned', live.totals.planned],
                ['Submitted', live.totals.submitted],
                ['Queued', live.totals.pending],
                ['Running', live.totals.in_progress],
                ['Done', live.totals.completed],
                ['Failed', live.totals.failed + live.totals.failed_to_submit],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="px-4 py-3">
                <div className="text-xs text-slate-500">{label}</div>
                <div className="text-lg font-semibold tabular-nums text-slate-900">{formatCount(value)}</div>
              </div>
            ))}
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
                <th className="px-5 py-2.5">Application</th>
                <th className="px-5 py-2.5">Submitted</th>
                <th className="px-5 py-2.5">Queued</th>
                <th className="px-5 py-2.5">Running</th>
                <th className="px-5 py-2.5">Done</th>
                <th className="px-5 py-2.5">Failed</th>
                <th className="px-5 py-2.5">Progress</th>
              </tr>
            </thead>
            <tbody>
              {live.applications.map((app) => {
                const done = app.completed + app.failed;
                const percent = app.planned > 0 ? Math.min(100, (done / app.planned) * 100) : 0;
                return (
                  <tr key={app.app} className="border-b border-slate-50 last:border-0">
                    <td className="px-5 py-3">
                      <span className="font-medium text-slate-900">{app.label}</span>
                      {app.error && (
                        <span className="block text-xs text-danger-700">{app.error}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      {formatCount(app.submitted)}
                      <span className="text-slate-400"> / {formatCount(app.planned)}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{formatCount(app.pending)}</td>
                    <td className="px-5 py-3 text-indigo-700 font-medium">
                      {formatCount(app.in_progress)}
                    </td>
                    <td className="px-5 py-3 tabular-nums text-success-700">{formatCount(app.completed)}</td>
                    <td className="px-5 py-3 text-slate-600">
                      {formatCount(app.failed + app.failed_to_submit)}
                    </td>
                    <td className="px-5 py-3 w-40">
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-500 transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400">{Math.round(percent)}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {run.error && (
            <div className="px-5 py-3 border-t border-slate-100 text-sm text-danger-700">
              {run.error}
            </div>
          )}
          {!running && (
            <div className="px-5 py-3 border-t border-slate-100 text-sm text-slate-500">
              Total test time {formatSeconds(
                run.started_at && run.finished_at
                  ? (new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000
                  : null,
              )}
              , start to last request finishing.{' '}
              <Link
                to={`/qa/history?run=${encodeURIComponent(run.id)}`}
                className="text-indigo-600 font-medium hover:text-indigo-700"
              >
                See the full breakdown
              </Link>
              .
            </div>
          )}
        </div>
      )}

      {recent.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-900">Recent runs</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
                {/* The build under test, not the test's own version: comparing two
                    runs of the same test is only meaningful once you know which
                    backend each one measured. The test version rides along with
                    its name in the next column. */}
                <th className="px-5 py-2.5">Backend</th>
                <th className="px-5 py-2.5">Test</th>
                <th className="px-5 py-2.5">Run</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5">Submitted</th>
                <th className="px-5 py-2.5">Started</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {recent.map((item) => (
                <tr key={item.id} className="border-b border-slate-50 last:border-0">
                  <td
                    className="px-5 py-3 font-mono text-xs text-slate-500"
                    title={
                      Object.entries(item.backend_versions || {})
                        .map(([app, version]) => `${labelFor(app)}: ${version}`)
                        .join('\n') || 'No backend version was recorded for this run'
                    }
                  >
                    {backendVersionLabel(item.backend_versions)}
                  </td>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    {item.definition_name}
                    <span className="ml-1.5 font-normal text-xs text-slate-400">
                      v{item.definition_version}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">#{item.run_number}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${runStatusClasses(item.status)}`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {formatCount(item.calls_submitted)}
                    <span className="text-slate-400"> / {formatCount(item.calls_planned)}</span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {formatTimestamp(item.started_at || item.created_at)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => {
                        setLive(null);
                        setActiveRunId(item.id);
                        setParams({ run: item.id }, { replace: true });
                      }}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      Monitor
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
